// @ts-nocheck

/**
 * Format a date to a readable string
 * @param date - Date object, timestamp, or Firebase Timestamp
 * @returns Formatted date string
 */
export const formatDate = (date: any): string => {
  if (!date) return '';
  
  try {
    let dateObj: Date;
    
    // Handle Firebase Timestamp
    if (date?.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } 
    // Handle Date object
    else if (date instanceof Date) {
      dateObj = date;
    }
    // Handle string date
    else if (typeof date === 'string') {
      dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        return date; // Return original string if can't parse
      }
    }
    // Handle number timestamp
    else if (typeof date === 'number') {
      dateObj = new Date(date);
    }
    // Handle object with seconds/nanoseconds (Firestore timestamp)
    else if (date?.seconds) {
      dateObj = new Date(date.seconds * 1000);
    }
    else {
      return String(date);
    }
    
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format a date to show time
 * @param date - Date object, timestamp, or Firebase Timestamp
 * @returns Formatted time string
 */
export const formatTime = (date: any): string => {
  if (!date) return '';
  
  try {
    let dateObj: Date;
    
    if (date?.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
    } else if (date?.seconds) {
      dateObj = new Date(date.seconds * 1000);
    } else {
      return '';
    }
    
    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

/**
 * Format a date to show relative time (e.g., "2 hours ago")
 * @param date - Date object, timestamp, or Firebase Timestamp
 * @returns Relative time string
 */
export const formatRelativeTime = (date: any): string => {
  if (!date) return '';
  
  try {
    let dateObj: Date;
    
    if (date?.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
    } else if (date?.seconds) {
      dateObj = new Date(date.seconds * 1000);
    } else {
      return '';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffDay > 7) {
      return formatDate(dateObj);
    } else if (diffDay > 0) {
      return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    } else if (diffHour > 0) {
      return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    } else if (diffMin > 0) {
      return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return formatDate(date);
  }
};

/**
 * Format currency amount
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'ZAR')
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: string = 'ZAR'): string => {
  try {
    // Use South African Rand formatting
    if (currency === 'ZAR') {
      return `R ${amount.toLocaleString('en-ZA', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}`;
    }
    
    // For other currencies, use Intl.NumberFormat
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `R${amount}`;
  }
};

/**
 * Format a number with K/M suffixes
 * @param num - The number to format
 * @returns Formatted number string
 */
export const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

/**
 * Truncate text with ellipsis
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format phone number
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  
  // South African phone number formatting
  if (cleaned.startsWith('27')) {
    return `+${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5, 8)} ${cleaned.substring(8)}`;
  }
  
  // International formatting
  if (cleaned.length > 10) {
    return `+${cleaned}`;
  }
  
  return cleaned;
};

/**
 * Format file size
 * @param bytes - Size in bytes
 * @returns Formatted file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format percentage
 * @param value - The percentage value (0-100)
 * @param decimals - Number of decimal places
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, decimals: number = 0): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format rating stars
 * @param rating - Rating value (0-5)
 * @param maxStars - Maximum number of stars
 * @returns Array of star icons
 */
export const formatRating = (rating: number, maxStars: number = 5): string[] => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = maxStars - fullStars - (hasHalfStar ? 1 : 0);
  
  // 🟢 FIXED: Explicitly typed as string[] so it's not never[]
  const stars: string[] = [];
  for (let i = 0; i < fullStars; i++) stars.push('★');
  if (hasHalfStar) stars.push('½');
  for (let i = 0; i < emptyStars; i++) stars.push('☆');
  
  return stars;
};

/**
 * Format order status with proper styling
 * @param status - Order status
 * @returns Object with text and color
 */
export const formatOrderStatus = (status: string) => {
  const statusMap: Record<string, { text: string; color: string }> = {
    'pending': { text: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
    'processing': { text: 'Processing', color: 'bg-blue-500/20 text-blue-400' },
    'shipped': { text: 'Shipped', color: 'bg-purple-500/20 text-purple-400' },
    'delivered': { text: 'Delivered', color: 'bg-green-500/20 text-green-400' },
    'cancelled': { text: 'Cancelled', color: 'bg-red-500/20 text-red-400' },
  };
  
  return statusMap[status] || { text: status, color: 'bg-gray-500/20 text-gray-400' };
};